import React, { useEffect } from "react";
import { ArrowRight, Sparkles, UtensilsCrossed, ChefHat, Clock, ListFilter, Compass, Instagram } from "lucide-react";
import "./Landing.css";
import { LanguageProvider, useLang } from "./i18n";
import { LanguageSwitch } from "../app/components/LanguageSwitch";
import { trackEvent } from "../app/services/analyticsService";

// Assets
import mainShot from "./assets/main.png";
import moodShot from "./assets/mood.png";
import mealTypeShot from "./assets/mealType.png";
import sliderShot from "./assets/slider.png";
import recipeShot from "./assets/recipe.png";
import ingredientsShot from "./assets/ingredients.png";
import descriptionShot from "./assets/description.png";
import logo from "./assets/logo.png";

const APP_URL = "/app/";

// Preload fonts
const FontPreload = () => (
  <React.Fragment>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Instrument+Serif&family=Playfair+Display:wght@400;500;600;700&family=Outfit:wght@500;600;700&display=swap" />
  </React.Fragment>
);

function CTAButton({ location = "hero" }) {
  const { t } = useLang();

  const handleClick = async (e) => {
    e.preventDefault();
    try {
      await trackEvent('landing_cta_click', {
        landing: 'use-ingredients',
        button: location,
        userId: 'anonymous'
      });
    } catch (err) {
      console.error('Failed to track event:', err);
    }
    window.location.href = APP_URL;
  };

  return (
    <a
      href={APP_URL}
      onClick={handleClick}
      className="group inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-base font-medium text-primary-foreground shadow-sm transition-all hover:shadow-md hover:brightness-110 active:scale-[0.98]"
    >
      {t("cta.try")}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
    </a>
  );
}

export default function Landing() {
  const { t } = useLang();

  const steps = [
    { n: "01", icon: Sparkles, title: t("how.1.title"), desc: t("how.1.desc"), src: moodShot },
    { n: "02", icon: ListFilter, title: t("how.2.title"), desc: t("how.2.desc"), src: mealTypeShot },
    { n: "03", icon: ChefHat, title: t("how.3.title"), desc: t("how.3.desc"), src: recipeShot },
  ];

  const benefits = [
    { icon: Clock, title: t("why.1.title"), desc: t("why.1.desc") },
    { icon: Compass, title: t("why.2.title"), desc: t("why.2.desc") },
    { icon: UtensilsCrossed, title: t("why.3.title"), desc: t("why.3.desc") },
  ];

  const previews = [
    { src: moodShot, caption: t("preview.cap.1") },
    { src: mealTypeShot, caption: t("preview.cap.2") },
    { src: sliderShot, caption: t("preview.cap.3") },
    { src: recipeShot, caption: t("preview.cap.4") },
    { src: ingredientsShot, caption: t("preview.cap.5") },
    { src: descriptionShot, caption: t("preview.cap.6") },
  ];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source');
    
    if (utmSource === 'ig' || params.has('fbclid')) {
      trackEvent('entry_from_instagram', {
        utm_source: utmSource || 'ig_inferred',
        landing: 'pivot-3',
        url: window.location.href,
        referrer: document.referrer,
        userId: 'anonymous'
      });
    }
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground landing-wrapper">
      <FontPreload />
      {/* Nav */}
      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-3">
          <img src={logo} alt="The Fridge logo" className="h-20 object-contain" />
          <span className="font-brand text-2xl font-medium">The Fridge</span>
        </div>
        <div className="flex items-center gap-6">
          <LanguageSwitch />
          <a
            href={APP_URL}
            onClick={async (e) => {
              e.preventDefault();
              try {
                await trackEvent('landing_cta_click', {
                  landing: 'pivot-3',
                  button: 'nav_open_app',
                  userId: 'anonymous'
                });
              } catch (err) {
                console.error('Failed to track event:', err);
              }
              window.location.href = APP_URL;
            }}
            className="hidden text-sm font-medium text-foreground/80 hover:text-foreground sm:inline-flex"
          >
            {t("nav.openApp")}
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-6 pt-8 pb-20 md:pt-16 md:pb-28">
        <div className="grid items-center gap-12 md:grid-cols-2 md:gap-16">
          <div>
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-accent/40 px-3 py-1 text-xs font-medium text-accent-foreground">
              <Sparkles className="h-3.5 w-3.5" /> {t("hero.badge")}
            </div>
            <h1 className="font-serif text-5xl leading-[1.05] tracking-tight text-foreground md:text-6xl lg:text-7xl">
              {t("hero.title.a")} <em className="italic text-primary">{t("hero.title.em")}</em>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
              {t("hero.desc")}
            </p>
            <div className="mt-8 flex flex-col items-start gap-3">
              <CTAButton location="hero_cta" />
              <p className="text-sm text-muted-foreground">{t("cta.availability")}</p>
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-sm md:max-w-md">
            <div className="absolute -inset-8 -z-10 rounded-[3rem] bg-gradient-to-br from-accent/60 via-background to-secondary/60 blur-2xl" />
            <img
              src={mainShot}
              alt="The Fridge app showing recipe suggestions"
              className="w-full drop-shadow-2xl"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-border bg-muted/40">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
          <div className="mb-14 max-w-2xl">
            <p className="mb-3 text-sm font-medium uppercase tracking-wider text-primary">{t("how.eyebrow")}</p>
            <h2 className="font-serif text-4xl tracking-tight md:text-5xl">{t("how.title")}</h2>
          </div>
          <div className="grid gap-12 md:grid-cols-3 md:gap-8">
            {steps.map(({ n, icon: Icon, title, desc, src }) => (
              <div key={n} className="flex flex-col">
                <div className="mb-5 flex items-center gap-3">
                  <span className="text-sm font-mono text-muted-foreground">{n}</span>
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </div>
                </div>
                <h3 className="text-lg font-semibold tracking-tight">{title}</h3>
                <p className="mt-2 text-base leading-relaxed text-muted-foreground">{desc}</p>
                {src && (
                  <img src={src} alt={title} className="mt-8 w-full max-w-[16rem] drop-shadow-xl" loading="lazy" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why it's useful */}
      <section className="mx-auto max-w-6xl px-6 py-20 md:py-28">
        <div className="mb-14 max-w-2xl">
          <p className="mb-3 text-sm font-medium uppercase tracking-wider text-primary">{t("why.eyebrow")}</p>
          <h2 className="font-serif text-4xl tracking-tight md:text-5xl">{t("why.title")}</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {benefits.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="rounded-2xl border border-border bg-card p-6">
              <Icon className="h-6 w-6 text-primary" strokeWidth={2} />
              <h3 className="mt-4 text-lg font-semibold tracking-tight">{title}</h3>
              <p className="mt-2 text-base leading-relaxed text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Product preview */}
      <section className="border-t border-border bg-muted/40">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
          <div className="mb-14 max-w-2xl">
            <p className="mb-3 text-sm font-medium uppercase tracking-wider text-primary">{t("preview.eyebrow")}</p>
            <h2 className="font-serif text-4xl tracking-tight md:text-5xl">{t("preview.title")}</h2>
          </div>

          <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-3 lg:gap-10">
            {previews.map(({ src, caption }, i) => (
              <figure key={i} className="flex flex-col items-center">
                <div className="relative w-full max-w-xs">
                  <div className="absolute -inset-6 -z-10 rounded-[3rem] bg-gradient-to-br from-accent/50 to-secondary/40 blur-xl" />
                  <img src={src} alt={caption} className="w-full drop-shadow-xl" loading="lazy" />
                </div>
                <figcaption className="mt-6 max-w-xs text-center text-sm text-muted-foreground">{caption}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Positioning / problem */}
      <section className="mx-auto max-w-3xl px-6 py-20 md:py-28">
        <p className="mb-3 text-sm font-medium uppercase tracking-wider text-primary">{t("problem.eyebrow")}</p>
        <h2 className="font-serif text-4xl leading-tight tracking-tight md:text-5xl">{t("problem.title")}</h2>
        <p className="mt-6 text-lg leading-relaxed text-muted-foreground">{t("problem.desc")}</p>
      </section>

      {/* Trust */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-3xl px-6 py-16 text-center md:py-20">
          <p className="font-serif text-2xl leading-relaxed text-foreground/80 md:text-3xl">
            {t("quote")}
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-3xl px-6 py-20 text-center md:py-28">
          <h2 className="font-serif text-4xl tracking-tight md:text-5xl">
            {t("final.title.a")} <br className="hidden sm:block" />
            <em className="italic text-primary">{t("final.title.em")}</em>
          </h2>
          <div className="mt-10 flex flex-col items-center gap-3">
            <CTAButton location="final_cta" />
            <p className="text-sm text-muted-foreground">{t("cta.availability")}</p>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-6 py-8 text-sm text-muted-foreground sm:flex-row">
          <div className="flex items-center gap-2">
            <img src={logo} alt="The Fridge logo" className="h-10 w-10 object-contain" />
            <span>The Fridge</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{t("social.follow")}</span>
            <a
                href="https://www.instagram.com/michal_the_fridge"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border text-foreground/70 transition-colors hover:bg-accent hover:text-foreground"
            >
              <Instagram className="h-6 w-6" />
            </a>
          </div>
          <p>© {new Date().getFullYear()} The Fridge</p>
        </div>
      </footer>
    </div>
  );
}
